import { Component, Input, Output, EventEmitter, ChangeDetectorRef, HostListener, OnChanges, SimpleChanges, ViewChild, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DomSanitizer, SafeUrl, SafeHtml } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { ImageViewerDialogComponent } from '../../image-viewer-dialog.component';
import { openDocumentPreview } from '../../document-preview-dialog.component';
import { MessageMenuComponent } from '../message-menu/message-menu.component';
import { QuickReactionMenuComponent } from '../../quick-reaction-menu/quick-reaction-menu.component';
import {
  isImage, isVideo, isAudio, isDocument, isURL, getFileType, getCleanFileName, stripTimestampPrefix,
  isSystemMessage, isGroupActionMessage, isMessageRedundantWithFile, getReplyThumbnail, getReplyMediaType,
  getReplyMessageText, getReplyMediaTypeIcon, getAggregatedReactions, isValidImageUrl,
  isGifMessage, getMessageMeta, isVoiceMessage, DOCUMENT_EXTENSIONS, getFileExtLabel, getFileBadgeColor
} from '../../utils/helpers.utils';
import { OnlineUsersService } from 'src/app/service/online-users.service';
import { downloadFile as saveFileInApp } from 'src/app/component/helpers/helper';


@Component({
  selector: 'app-chat-message-item',
  standalone: true,
  imports: [CommonModule, MessageMenuComponent, QuickReactionMenuComponent],
  templateUrl: './chat-message-item.component.html',
  styleUrls: ['./chat-message-item.component.css'],
  providers: [DatePipe]
})
export class ChatMessageItemComponent implements OnChanges, OnDestroy {
  @Input() message: any;
  @Input() userId: string;
  @Input() chatMap: { [key: string]: any } = {};
  @Input() userNameMapping: { [key: string]: string } = {};
  @Input() profileImageMapping: { [key: string]: string } = {};
  @Input() pinnedMessages: any[] = [];
  @Input() starredMessages: any[] = [];
  @Input() activeMessageMenuId: string | null = null;
  @ViewChild(MessageMenuComponent) messageMenu?: MessageMenuComponent;

  contextMenuTop: number | null = null;
  contextMenuLeft: number | null = null;
  @Input() isMobileView: boolean = false;
  @Input() showcount: boolean = true;
  @Input() commonReactions: string[] = ['👍', '❤️', '😂', '😮', '🙏'];
  @Input() reactingToMessageId: string | null = null;
  @Input() isGroupChat: boolean = false;
  @Input() isNoLongerMember: boolean = false;
  @Input() uploadProgress: number = 0;
  @Input() channelId: string = '';
  showVersionInfo: boolean = false;
  isLoadingVersionInfo: boolean = false;
  isPlaying: boolean = false;
  playbackSpeed: number = 1;
  audioDuration: string = '';
  totalDurationRaw: number = 0;
  currentTime: number = 0;
  currentTimeDisplay: string = '0:00';
  animationFrameId: any;
  waveformBars: number[] = [35, 65, 40, 85, 55, 30, 75, 100, 60, 40, 80, 50, 95, 70, 35, 75, 90, 50, 85, 45, 65, 100, 75, 45, 85, 60, 35, 70, 50, 80, 45, 60];

  private displayMessageCache = new Map<string, SafeHtml>();
  private followUpTextCache = new Map<string, SafeHtml>();
  revealedSpoilers = new Set<string>();

  constructor(
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private onlineUsersService: OnlineUsersService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['message']) {
      const msg = changes['message'].currentValue;
      const prevMsg = changes['message'].previousValue;

      const msgId = msg?._id || msg?.messageId || msg?.id;
      const prevMsgId = prevMsg?._id || prevMsg?.messageId || prevMsg?.id;
      const isSameMessage = prevMsg && msgId && prevMsgId && msgId === prevMsgId;

      if (prevMsg && (msg?.message !== prevMsg?.message || msg?.meta?.caption !== prevMsg?.meta?.caption || msg?.systemText !== prevMsg?.systemText)) {
        this.displayMessageCache.clear();
        this.followUpTextCache.clear();
      }

      const storedDuration = msg?.meta?.duration || msg?.duration || msg?.file?.duration || (msg?.files && msg.files[0]?.duration);
      if (storedDuration && storedDuration > 0) {
        this.totalDurationRaw = storedDuration;
        this.audioDuration = this.formatDuration(storedDuration);
      } else if (isSameMessage && this.totalDurationRaw > 0) {
        if (!msg.meta) msg.meta = {};
        msg.meta.duration = this.totalDurationRaw;
      } else {
        // Reset for new messages with no stored duration
        this.totalDurationRaw = 0;
        this.audioDuration = '';
      }
    }

    if (changes['activeMessageMenuId']) {
      const myId = this.message?._id || this.message?.id || this.message?.messageId;
      if (this.activeMessageMenuId !== myId) {
        this.contextMenuTop = null;
        this.contextMenuLeft = null;
      }
    }
  }

  toggleVersionInfo(event: MouseEvent) {
    event.stopPropagation();
    this.showVersionInfo = !this.showVersionInfo;

    // Only fetch if thread is missing or empty to avoid overwriting real-time data from sendChatMessage
    if (this.showVersionInfo && this.message.meta?.version && (!this.message.meta.thread || this.message.meta.thread.length === 0)) {
      this.fetchVersionThread();
    }
  }

  fetchVersionThread() {
    const url = this.message.url || this.message.meta?.fileUrl || this.message.meta?.gifUrl || this.message.message;
    if (!url || !isURL(url)) return;

    this.isLoadingVersionInfo = true;
    const rawChannelId = this.message.meta?.channel_id || this.message.channel_id || this.message.chatId || this.message.meta?.groupId || this.channelId;

    if (!rawChannelId) {
      console.warn('Cannot fetch version thread: No channelId found in message context');
      this.isLoadingVersionInfo = false;
      return;
    }

    const channelId = String(rawChannelId).trim();

    const body: any = {
      url: url,
      channelId: channelId,
    };

    if (this.message.meta?.fileId) {
      body.fileId = this.message.meta.fileId;
    }

    this.onlineUsersService.isUrlValid(body).subscribe({
      next: (res: any) => {
        if (res?.data && res.data.thread) {
          if (!this.message.meta) this.message.meta = {};
          this.message.meta.thread = res.data.thread;
          this.cdRef.detectChanges();
        }
        this.isLoadingVersionInfo = false;
      },
      error: (err) => {
        console.error('Error fetching version thread:', err);
        this.isLoadingVersionInfo = false;
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.showVersionInfo = false;
  }

  toggleAudio(event: MouseEvent, player: HTMLAudioElement) {
    event.stopPropagation();
    if (!player) return;
    if (this.isPlaying) {
      player.pause();
      this.currentTime = player.currentTime;
      this.currentTimeDisplay = this.formatDuration(player.currentTime);
      if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    } else {
      player.playbackRate = this.playbackSpeed;
      player.play().catch(err => console.warn('Audio playback error:', err));
      this.updateProgressSmoothly(player);
    }
    this.isPlaying = !this.isPlaying;
    this.cdRef.detectChanges();
  }

  toggleAudioSpeed(event: MouseEvent, player: HTMLAudioElement) {
    event.stopPropagation();
    const speeds = [1, 1.5, 2];
    const currentIndex = speeds.indexOf(this.playbackSpeed);
    this.playbackSpeed = speeds[(currentIndex + 1) % speeds.length];
    if (player) {
      player.playbackRate = this.playbackSpeed;
    }
    this.cdRef.detectChanges();
  }

  updateProgressSmoothly(player: HTMLAudioElement) {
    const update = () => {
      if (!player.paused && !player.ended) {
        this.currentTime = player.currentTime;
        this.currentTimeDisplay = this.formatDuration(player.currentTime);
        this.cdRef.detectChanges();
        this.animationFrameId = requestAnimationFrame(update);
      }
    };
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = requestAnimationFrame(update);
  }

  onAudioEnded() {
    this.isPlaying = false;
    this.currentTime = 0;
    this.currentTimeDisplay = '0:00';
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    this.cdRef.detectChanges();
  }

  onMetadataLoaded(player: HTMLAudioElement) {
    if (player && player.duration && isFinite(player.duration)) {
      this.totalDurationRaw = player.duration;
      this.audioDuration = this.formatDuration(player.duration);

      // Always sync duration back to message meta for future forwards
      if (this.message) {
        if (!this.message.meta) this.message.meta = {};
        this.message.meta.duration = player.duration;
      }

      this.cdRef.detectChanges();
    }
  }

  onTimeUpdate(player: HTMLAudioElement) {
    if (player) {
      this.currentTime = player.currentTime;
      this.currentTimeDisplay = this.formatDuration(player.currentTime);
      this.cdRef.detectChanges();
    }
  }

  onSeek(event: any, player: HTMLAudioElement) {
    const time = parseFloat(event.target.value);
    if (!isNaN(time)) {
      this.currentTime = time;
      this.currentTimeDisplay = this.formatDuration(time);
      if (player) {
        player.currentTime = time;
      }
      this.cdRef.detectChanges();
    }
  }

  getAudioAvatar(): string {
    if (!this.message) return 'assets/user-icon.png';
    if (this.userId === this.message.sender_id) {
      return this.profileImageMapping[this.userId] || 'assets/user-icon.png';
    }
    return this.profileImageMapping[this.message.sender_id] || this.chatMap[this.channelId]?.profileImage || 'assets/user-icon.png';
  }

  isAudioVoiceMessage(file?: any): boolean {
    if (file?.name && file.name.includes('voice_message_')) return true;
    if (file?.fileUrl && isVoiceMessage(file.fileUrl)) return true;
    return this.isVoiceMessage(this.message);
  }

  getAudioFileUrl(file?: any): string {
    if (file?.fileUrl) return file.fileUrl;
    const files = this.getFilesFromMessage(this.message);
    if (files && files.length > 0 && files[0]?.fileUrl) return files[0].fileUrl;
    return this.message?.meta?.fileUrl || this.message?.fileUrl || '';
  }

  isAudioMessage(message: any): boolean {
    if (!message) return false;
    if (this.isVoiceMessage(message)) return true;
    const files = this.getFilesFromMessage(message);
    if (files && files.length > 0) {
      return files.some(f => f.type?.startsWith('audio/') || isAudio(f.fileUrl) || (f.name && f.name.includes('voice_message_')));
    }
    return false;
  }

  getProgressPercent(): number {
    const dur = this.totalDurationRaw || this.message?.meta?.duration || this.message?.duration || this.message?.file?.duration || 0;
    if (!dur || dur <= 0 || isNaN(dur)) {
      return 0;
    }
    const pct = (this.currentTime / dur) * 100;
    return Math.max(0, Math.min(100, isNaN(pct) ? 0 : pct));
  }

  getAudioTotalDuration(): string {
    if (this.audioDuration && this.audioDuration !== '0:00') {
      return this.audioDuration;
    }
    const dur = this.totalDurationRaw || this.message?.meta?.duration || this.message?.duration || this.message?.file?.duration || (this.message?.files && this.message.files[0]?.duration);
    if (dur && dur > 0) {
      return this.formatDuration(dur);
    }
    return '0:00';
  }

  formatDuration(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  @Output() reply = new EventEmitter<any>();
  @Output() forward = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<{ message: any, forEveryone: boolean }>();
  @Output() pin = new EventEmitter<any>();
  @Output() unpin = new EventEmitter<any>();
  @Output() star = new EventEmitter<any>();
  @Output() unstar = new EventEmitter<any>();
  @Output() reaction = new EventEmitter<{ emoji: string, messageId: string }>();
  @Output() scrollToMessage = new EventEmitter<any>();
  @Output() fullReactionPicker = new EventEmitter<string>();
  @Output() toggleMenu = new EventEmitter<{ messageId: string, messageObj: any }>();
  @Output() openReactionDetails = new EventEmitter<any>();
  @Output() alertClick = new EventEmitter<any>();
  @Output() seenInfo = new EventEmitter<any>();
  @Output() userMentionClick = new EventEmitter<string>();
  @Output() allMentionClick = new EventEmitter<void>();
  @Output() replyPrivately = new EventEmitter<any>();
  @Output() memberClick = new EventEmitter<string>();

  onMemberClick(senderId?: string): void {
    if (!senderId || String(senderId) === String(this.userId)) return;
    this.memberClick.emit(senderId);
  }

  onGroupSourceClick(event: MouseEvent, replyTo: any): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!replyTo) return;
    const targetId = replyTo.id || replyTo._id || replyTo.messageId;
    this.scrollToMessage.emit({
      id: targetId,
      replyTo: replyTo,
      forceGroup: true
    });
  }

  @HostListener('click', ['$event'])
  onItemClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Telegram spoiler click to reveal / hide
    const spoilerEl = target?.closest('.tg-spoiler') as HTMLElement;
    if (spoilerEl) {
      if (spoilerEl.classList.contains('revealed') && target.tagName.toLowerCase() === 'a') {
        // Allow link click inside revealed spoiler
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const key = spoilerEl.getAttribute('data-spoiler-key');
      if (key) {
        if (this.revealedSpoilers.has(key)) {
          this.revealedSpoilers.delete(key);
          spoilerEl.classList.remove('revealed');
          spoilerEl.setAttribute('title', 'Click to reveal');
        } else {
          this.revealedSpoilers.add(key);
          spoilerEl.classList.add('revealed');
          spoilerEl.setAttribute('title', 'Click to hide');
        }
        this.displayMessageCache.clear();
      } else {
        const isRevealed = spoilerEl.classList.toggle('revealed');
        spoilerEl.setAttribute('title', isRevealed ? 'Click to hide' : 'Click to reveal');
      }
      return;
    }

    const mentionEl = target?.closest('.mention-highlight') as HTMLElement;
    if (mentionEl) {
      event.preventDefault();
      event.stopPropagation();
      const type = mentionEl.getAttribute('data-mention-type');
      if (type === 'all' || mentionEl.textContent?.trim().toLowerCase() === '@all') {
        this.allMentionClick.emit();
      } else {
        const userId = mentionEl.getAttribute('data-user-id') || '';
        const name = mentionEl.getAttribute('data-mention-name') || mentionEl.textContent?.replace('@', '').trim() || '';
        this.userMentionClick.emit(userId || name);
      }
    }
  }
  @Output() makeTask = new EventEmitter<any>();

  onReactionContainerClick(event: MouseEvent) {
    event.stopPropagation();
    this.openReactionDetails.emit(this.message);
  }

  onContextMenu(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'AUDIO' || target.tagName === 'VIDEO' || target.closest('a'))) {
      return;
    }

    // 1. If text is currently selected, allow native copying/selection menu without overriding
    const currentSelection = (typeof window !== 'undefined' && window.getSelection) ? window.getSelection()?.toString() : '';
    if (currentSelection && currentSelection.trim().length > 0) {
      return;
    }

    // 2. On mobile/touch devices: long-pressing message text MUST allow native selection and copying
    const isTouchOrMobile = this.isMobileView || (typeof window !== 'undefined' && (
      Boolean(window.matchMedia?.('(pointer: coarse)').matches) ||
      ('ontouchstart' in window && !window.matchMedia?.('(pointer: fine)').matches)
    ));
    const isTextTarget = target && !!target.closest('.msg-txt, .text-container, .media-caption, .chat-text-content, .reply-inner-wrapper, p, span, pre, code');

    if (isTouchOrMobile && isTextTarget) {
      // Allow native mobile text selection handles and clipboard actions
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const msgId = this.message?._id || this.message?.id || this.message?.messageId;
    if (msgId) {
      const bubbleEl = event.currentTarget as HTMLElement;
      if (bubbleEl) {
        const bubbleRect = bubbleEl.getBoundingClientRect();
        this.contextMenuTop = Math.max(0, Math.min(bubbleRect.height - 10, event.clientY - bubbleRect.top));
        this.contextMenuLeft = Math.max(0, Math.min(bubbleRect.width - 10, event.clientX - bubbleRect.left));
      }
      if (this.activeMessageMenuId !== msgId) {
        this.toggleMenu.emit({ messageId: msgId, messageObj: this.message });
      } else if (this.messageMenu) {
        this.messageMenu.calculatePosition();
      }
      this.cdRef.detectChanges();
    }
  }

  onMenuTriggerClick(event: MouseEvent) {
    event.stopPropagation();
    this.contextMenuTop = null;
    this.contextMenuLeft = null;
    const msgId = this.message?._id || this.message?.id || this.message?.messageId;
    if (msgId) {
      this.toggleMenu.emit({ messageId: msgId, messageObj: this.message });
    }
  }

  get shouldShowDateHeader(): boolean {
    return this.message?.showDateHeader || this.message?.showGroupDateHeader;
  }

  isToday(date: any): boolean {
    if (!date) return false;
    const d = new Date(date);
    const today = new Date();
    return d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear();
  }

  isYesterday(date: any): boolean {
    if (!date) return false;
    const d = new Date(date);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return d.getDate() === yesterday.getDate() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getFullYear() === yesterday.getFullYear();
  }

  expandedMessages = new Set<string>();

  isSystemMessage(msg: any): boolean {
    return isSystemMessage(msg);
  }

  isMessageSeen(message: any): boolean {
    if (!message) return false;
    if (message.seen === true) return true;
    if (message.status === 'read' || message.status === 'seen') return true;
    if (message.is_seen === true) return true;
    return false;
  }

  isAlertMessage(msg: any): boolean {
    if (!msg) return false;

    // Group-related action notify messages must NEVER show the alert link ("- click for more info...")
    if (isGroupActionMessage(msg)) {
      return false;
    }

    const text = typeof msg === 'string' ? msg : (msg.message || msg.systemText || '');
    const meta = msg.meta || {};

    // Task lines always open the task
    if (meta.module === 'tasks' && meta.redirectUrl) return true;

    if (typeof text === 'string') {
      const lower = text.toLowerCase();

      if (
        lower.includes('alert:') ||
        lower.includes('updated the status of') ||
        lower.includes('reassigned') ||
        lower.includes('assigned a lead') ||
        lower.includes('lead assigned') ||
        lower.includes('follow-up') ||
        lower.includes('follow up') ||
        lower.includes('closing pool') ||
        lower.includes('lead') ||
        lower.includes('project') ||
        /\bpi\b/.test(lower) ||
        /\bpo\b/.test(lower) ||
        lower.includes('invoice') ||
        lower.includes('vendor') ||
        lower.includes('payment') ||
        lower.includes('expense')
      ) {
        return true;
      }
    }

    return (
      meta.subType === 'Lead Assigned' ||
      meta.subType === 'Lead Status' ||
      meta.subType === 'Lead Status Update' ||
      meta.subType === 'Lead Reassigned' ||
      meta.subType === 'Lead Follow Up Assigned' ||
      meta.subType === 'Follow Up' ||
      meta.subType === 'Closing Pool Update' ||
      meta.subType === 'PI Request' ||
      meta.subType === 'PI Raised' ||
      meta.subType === 'PI Draft' ||
      meta.subType === 'PI Draft Approved' ||
      meta.subType === 'PI Draft Changes' ||
      meta.subType === 'PO Upload' ||
      meta.subType === 'Invoice Request' ||
      meta.subType === 'Invoice Raised' ||
      meta.subType === 'Vendor Payment Request' ||
      meta.subType === 'Payment Received' ||
      meta.subType === 'GST Deposited' ||
      meta.subType === 'TDS Received' ||
      meta.subType === 'Recurring Invoice' ||
      meta.subType === 'Expense Submitted' ||
      meta.event === 'Lead Assigned' ||
      meta.event === 'Lead Reassigned' ||
      meta.event === 'Lead Follow Up Assigned' ||
      meta.event === 'Status Update' ||
      meta.event === 'Closing Pool Update' ||
      meta.module === 'finance' ||
      meta.module === 'lead-tracking' ||
      meta.module === 'lead-track' ||
      meta.module === 'crm' ||
      meta.type === 'finance' ||
      msg.type === 'finance' ||
      (!!meta.redirectUrl && !meta.redirectUrl.includes('/main-chat'))
    );
  }

  getModuleInfo(message: any): { label: string; code: string } | null {
    if (!message) return null;
    const meta = message.meta || {};
    const moduleRaw = (meta.module || message.module || '').toLowerCase().trim();
    const typeRaw = (meta.type || message.type || '').toLowerCase().trim();
    const subTypeRaw = (meta.subType || meta.event || '').toLowerCase().trim();
    const textRaw = (message.systemText || message.message || '').toLowerCase().trim();
    const redirectUrl = (meta.redirectUrl || message.redirectUrl || '').toLowerCase().trim();

    // 0. Tasks (18 Oct 2026): status lines a task writes into its chat
    if (moduleRaw === 'tasks' || redirectUrl.includes('#/tasks?task=')) {
      return { label: 'TASK', code: 'tasks' };
    }

    // 1. Explicit Finance module or type or redirect
    if (
      moduleRaw === 'finance' ||
      typeRaw === 'finance' ||
      redirectUrl.includes('/finance') ||
      redirectUrl.includes('#/finance')
    ) {
      return { label: 'FIN.', code: 'finance' };
    }

    // 2. Explicit Lead Track module or type or redirect
    if (
      moduleRaw === 'lead-tracking' ||
      moduleRaw === 'lead-track' ||
      moduleRaw === 'lead_tracking' ||
      moduleRaw === 'leadtrack' ||
      moduleRaw === 'lead' ||
      redirectUrl.includes('lead-track') ||
      redirectUrl.includes('#/lead-track')
    ) {
      return { label: 'L.T', code: 'lead-track' };
    }

    // 3. SubType / Event detection
    if (
      subTypeRaw.includes('pi request') ||
      subTypeRaw.includes('pi raised') ||
      subTypeRaw.includes('po upload') ||
      subTypeRaw.includes('invoice request') ||
      subTypeRaw.includes('invoice raised') ||
      subTypeRaw.includes('vendor payment') ||
      subTypeRaw.includes('expense') ||
      subTypeRaw.includes('project created')
    ) {
      return { label: 'FIN.', code: 'finance' };
    }

    if (
      subTypeRaw.includes('lead') ||
      subTypeRaw.includes('follow-up') ||
      subTypeRaw.includes('follow up') ||
      subTypeRaw.includes('followup') ||
      meta.leadId ||
      meta.leadIdentifier ||
      meta.leadLabel
    ) {
      return { label: 'L.T', code: 'lead-track' };
    }

    // 4. Other explicitly known modules
    if (moduleRaw === 'crm') return { label: 'CRM', code: 'crm' };
    if (moduleRaw === 'inventory') return { label: 'Inventory', code: 'inventory' };

    // 5. Text heuristics for Finance
    if (
      textRaw.includes('new project') ||
      textRaw.includes('requested a pi') ||
      textRaw.includes('raised pi') ||
      textRaw.includes('uploaded po') ||
      textRaw.includes('requested an invoice') ||
      textRaw.includes('requested invoice') ||
      textRaw.includes('raised invoice') ||
      textRaw.includes('vendor payment') ||
      textRaw.includes('expense submitted') ||
      textRaw.includes('new expense') ||
      textRaw.includes('payment received') ||
      textRaw.includes('payment collected')
    ) {
      return { label: 'FIN.', code: 'finance' };
    }

    // 6. Text heuristics for Lead Track
    if (
      textRaw.includes('lead') ||
      textRaw.includes('follow-up') ||
      textRaw.includes('follow up') ||
      textRaw.includes('assigned a new') ||
      textRaw.includes('reassigned') ||
      textRaw.includes('updated the status of')
    ) {
      return { label: 'L.T', code: 'lead-track' };
    }

    return null;
  }

  getStatusBadgeStyle(status: string): { textColor: string; bgColor: string; borderColor: string } {
    const s = (status || '').trim().toLowerCase();

    // 1. Lost (Red)
    if (s.includes('lost')) {
      return { textColor: '#f30a0a', bgColor: '#fee2e2', borderColor: '#f30a0a' };
    }

    // 2. Closed (Green)
    if (s.includes('closed')) {
      return { textColor: '#16a34a', bgColor: '#dcfce7', borderColor: '#27d467' };
    }

    // 3. Unqualified (Orange)
    if (s.includes('unqualified')) {
      return { textColor: '#ea580c', bgColor: '#ffedd5', borderColor: '#fdba74' };
    }

    // 4. Contacted / Outreach Done (Blue)
    if (s.includes('contacted') || s.includes('outreach')) {
      return { textColor: '#053c47', bgColor: '#9be7f1', borderColor: '#bfdbfe' };
    }

    // 5. Email introduction (Sky Blue)
    if (s.includes('email')) {
      return { textColor: '#0518c1ff', bgColor: '#e0f2fe', borderColor: '#bae6fd' };
    }

    // 6. Meetings in Video call (Purple)
    if (s.includes('video')) {
      return { textColor: '#7c3aed', bgColor: '#ede9fe', borderColor: '#ddd6fe' };
    }

    // 7. Meetings in person / Introduction Meeting (Violet)
    if (s.includes('person') || s.includes('meeting')) {
      return { textColor: '#9333ea', bgColor: '#f3e8ff', borderColor: '#e9d5ff' };
    }

    // 8. Presentation / Deck (Teal)
    if (s.includes('presentation') || s.includes('deck')) {
      return { textColor: '#f50796', bgColor: '#fbccf0', borderColor: '#d65bc2' };
    }

    // 9. Cost sheet (Amber / Gold)
    if (s.includes('cost') || s.includes('sheet')) {
      return { textColor: '#d97706', bgColor: '#fef3c7', borderColor: '#fde68a' };
    }

    // 10. Closing Pool (Pink / Rose)
    if (s.includes('closing') || s.includes('pool')) {
      return { textColor: '#db2777', bgColor: '#fce7f3', borderColor: '#fbcfe8' };
    }

    // 11. Qualified (Emerald)
    if (s.includes('qualified')) {
      return { textColor: '#059669', bgColor: '#d1fae5', borderColor: '#6ee7b7' };
    }

    // 12. On Hold (Yellow-Brown)
    if (s.includes('hold')) {
      return { textColor: '#ca8a04', bgColor: '#fef9c3', borderColor: '#fde047' };
    }

    // 13. Follow-up / Following up (Indigo)
    if (s.includes('follow')) {
      return { textColor: '#4f46e5', bgColor: '#e0e7ff', borderColor: '#c7d2fe' };
    }

    // 14. Reassigned (Berry Wine / Crimson)
    if (s.includes('reassign')) {
      return { textColor: '#9f1239', bgColor: '#fff1f2', borderColor: '#fb7185' };
    }

    // 15. Not started / Assigned (Slate)
    if (s.includes('not started') || s.includes('assigned')) {
      return { textColor: '#475569', bgColor: '#f1f5f9', borderColor: '#cbd5e1' };
    }

    // Default fallback
    return { textColor: '#3b82f6', bgColor: '#eff6ff', borderColor: '#dbeafe' };
  }

  getSystemMessageData(message: any): {
    isStatusUpdate: boolean;
    isAlert: boolean;
    isFollowUpAlert: boolean;
    isClosedStatus: boolean;
    isLostStatus: boolean;
    alertTitle: string;
    alertBody: string;
    prefixText: string;
    status: string;
    timeText: string;
    statusStyle: { textColor: string; bgColor: string; borderColor: string };
  } {
    const rawText = (message?.systemText || message?.message || '').trim();
    const meta = message?.meta || {};
    const statusUpdateRegex = /^(.*?\bupdated the status of\b.*?\bto\s+)["']?([\w\s\-]+?)["']?(?:\s*(\(\s*\d{1,2}:\d{2}.*?\)))?$/i;
    const match = rawText.match(statusUpdateRegex);

    if (match) {
      const prefixText = match[1];
      const status = (match[2] || '').trim();
      const timeText = match[3] || '';
      const isClosed = status.toLowerCase().includes('closed');
      const isLost = status.toLowerCase().includes('lost');
      return {
        isStatusUpdate: true,
        isAlert: false,
        isFollowUpAlert: false,
        isClosedStatus: isClosed,
        isLostStatus: isLost,
        alertTitle: '',
        alertBody: '',
        prefixText: prefixText,
        status: status,
        timeText: timeText,
        statusStyle: this.getStatusBadgeStyle(status)
      };
    }

    // Check for Alert prefix
    const alertMatch = rawText.match(/^(Alert\s*:?\s*)(.*)$/i);
    if (alertMatch) {
      const alertTitle = alertMatch[1];
      const alertBody = alertMatch[2];
      const lower = rawText.toLowerCase();
      const isFollowUp =
        lower.includes('follow-up') ||
        lower.includes('follow up') ||
        lower.includes('followup') ||
        meta.event === 'Lead Follow Up Assigned' ||
        meta.subType === 'Lead Follow Up Assigned' ||
        meta.subType === 'Follow Up' ||
        meta.isFollowUp;

      return {
        isStatusUpdate: false,
        isAlert: true,
        isFollowUpAlert: isFollowUp,
        isClosedStatus: false,
        isLostStatus: false,
        alertTitle: alertTitle,
        alertBody: alertBody,
        prefixText: '',
        status: '',
        timeText: '',
        statusStyle: this.getStatusBadgeStyle('')
      };
    }

    // Fallback: If meta indicates status update or meta.status is present
    if (meta.subType === 'Lead Status' || meta.subType === 'Lead Status Update' || meta.status || meta.newStatus) {
      const status = meta.status || meta.newStatus || '';
      if (status && rawText.includes(status)) {
        const parts = rawText.split(status);
        const isClosed = status.toLowerCase().includes('closed');
        const isLost = status.toLowerCase().includes('lost');
        return {
          isStatusUpdate: true,
          isAlert: false,
          isFollowUpAlert: false,
          isClosedStatus: isClosed,
          isLostStatus: isLost,
          alertTitle: '',
          alertBody: '',
          prefixText: parts[0],
          status: status,
          timeText: parts.slice(1).join(status).trim(),
          statusStyle: this.getStatusBadgeStyle(status)
        };
      }
    }

    return {
      isStatusUpdate: false,
      isAlert: false,
      isFollowUpAlert: false,
      isClosedStatus: false,
      isLostStatus: false,
      alertTitle: '',
      alertBody: rawText,
      prefixText: rawText,
      status: '',
      timeText: '',
      statusStyle: this.getStatusBadgeStyle('')
    };
  }

  formatFollowUpText(text: string, message?: any): any {
    if (!text) return '';

    if (this.followUpTextCache.has(text)) {
      return this.followUpTextCache.get(text);
    }

    let escaped = this.escapeHtml(text);

    // Style timestamp ( hh:mm ... ) if embedded in message
    escaped = escaped.replace(
      /(\(\s*\d{1,2}:\d{2}(?:\s*(?:AM|PM|am|pm))?(?:\s*-\s*[A-Za-z]{3}\s*\d{1,2},\s*\d{4})?\s*\))/g,
      '<span class="system-message-time" style="font-size:10.5px;color:#787c8e;font-weight:400;margin-left:3px;">$1</span>'
    );

    // Highlight and truncate quoted pinned message with ellipsis
    escaped = escaped.replace(
      /(pinned\s+)(?:&quot;|["“])([^"”<]+)(?:&quot;|["”])/gi,
      (match, prefix, content) => {
        return `${prefix}<span class="pinned-quote-text" title="${content}">&ldquo;${content}&rdquo;</span>`;
      }
    );

    // Highlight follow-up word with status badge styling (Indigo)
    let highlighted = escaped.replace(
      /\b(follow[\s\-]up|followup|following[\s\-]up)\b/gi,
      '<span class="status-pill-badge lt-badge-flw follow-up-pill" style="color:#4f46e5;background-color:#e0e7ff;border:1px solid #c7d2fe;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // Highlight reassigned word with unique Berry Wine status badge styling
    highlighted = highlighted.replace(
      /\b(re[\s\-]assigned|reassigned|re[\s\-]assign|reassign|reassigning)\b/gi,
      '<span class="status-pill-badge lt-badge-ras reassigned-pill" style="color:#9f1239;background-color:#fff1f2;border:1px solid #fb7185;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // 1. Finance: Vendor Payment Request / Vendor Payment (Rose / Crimson)
    highlighted = highlighted.replace(
      /\b(vendor\s+payment\s+request|vendor\s+payment)\b/gi,
      '<span class="status-pill-badge f-badge-vnd finance-pill-vendor" style="color:#be123c;background-color:#ffe4e6;border:1px solid #fda4af;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // 2. Finance: New Project (Emerald / Mint Green)
    highlighted = highlighted.replace(
      /\b(new\s+project)\b/gi,
      '<span class="status-pill-badge f-badge-prj finance-pill-project" style="color:#047857;background-color:#d1fae5;border:1px solid #6ee7b7;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // 3. Finance: Payment Received / Payment Collected (Vibrant Green)
    highlighted = highlighted.replace(
      /\b(payment\s+received|payment\s+collected)\b/gi,
      '<span class="status-pill-badge f-badge-pmt finance-pill-payment" style="color:#15803d;background-color:#dcfce7;border:1px solid #86efac;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // 4. Finance: Expense Submitted / New Expense / Expense (Fuchsia / Magenta)
    highlighted = highlighted.replace(
      /\b(new\s+expense|expense\s+submitted|\bexpense\b)\b/gi,
      '<span class="status-pill-badge f-badge-exp finance-pill-expense" style="color:#a21caf;background-color:#fae8ff;border:1px solid #f0abfc;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // 5. Finance: Invoice / Invoice #... (Amber / Warm Gold)
    highlighted = highlighted.replace(
      /\b(invoice\s*#\s*[\w\-]+|\binvoice\b)\b/gi,
      '<span class="status-pill-badge f-badge-inv finance-pill-invoice" style="color:#b45309;background-color:#fef3c7;border:1px solid #fcd34d;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // 6. Finance: PI / PI #... (Sky Blue, case-sensitive)
    highlighted = highlighted.replace(
      /\b(PI\s*#\s*[\w\-]+|\bPI\b)/g,
      '<span class="status-pill-badge f-badge-pi finance-pill-pi" style="color:#0369a1;background-color:#e0f2fe;border:1px solid #7dd3fc;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    // 7. Finance: PO / PO #... (Indigo / Cobalt, case-sensitive)
    highlighted = highlighted.replace(
      /\b(PO\s*#\s*[\w\-]+|\bPO\b)/g,
      '<span class="status-pill-badge f-badge-po finance-pill-po" style="color:#4338ca;background-color:#e0e7ff;border:1px solid #a5b4fc;font-weight:700;padding:1px 8px;border-radius:12px;display:inline-flex;align-items:center;line-height:1.4;margin:0 3px;vertical-align:middle;">$1</span>'
    );

    const safeHtml = this.sanitizer.bypassSecurityTrustHtml(highlighted);
    this.followUpTextCache.set(text, safeHtml);
    return safeHtml;
  }

  onAlertLinkClick(event: Event) {
    this.alertClick.emit({ event, message: this.message });
  }

  isValidImageUrl(url: string): boolean {
    return isValidImageUrl(url);
  }

  isURL(str: string): boolean {
    return isURL(str);
  }

  isImage(url: string | any): boolean {
    return isImage(url);
  }

  isDocument(url: string | any): boolean {
    return isDocument(url);
  }

  isVideo(url: string | any): boolean {
    return isVideo(url);
  }

  isAudio(url: string | any, name?: string): boolean {
    return isAudio(url, name);
  }

  isVoiceMessage(message: any): boolean {
    return isVoiceMessage(message);
  }

  getFileType(filename: any): string {
    return getFileType(filename);
  }


  getSafeIcon(url: string): string {
    if (!url) return 'assets/fileAttachment.png';
    const lower = url.toLowerCase();
    if (lower.endsWith('.pdf')) return 'assets/pdf.png';
    if (lower.match(/\.(doc|docx)$/)) return 'assets/docx-img.png';
    if (lower.match(/\.(xls|xlsx)$/)) return 'assets/excel.png';
    if (lower.match(/\.(ppt|pptx)$/)) return 'assets/ppt-file-img.png';
    if (lower.match(/\.(mp3|wav|ogg|m4a|flac|aac)$/)) return 'assets/audio-thumbnail.png';
    if (this.isImage(url)) return url;
    return 'assets/docs.png';
  }

  hasReactions(message: any): boolean {
    return message.reactions && message.reactions.length > 0;
  }

  isMessagePinned(messageId: string): boolean {
    if (!messageId) return false;
    return this.pinnedMessages.some(m =>
      (m._id || m.id || m.messageId) === messageId ||
      (m.message && (m.message._id || m.message.id || m.message.messageId) === messageId)
    );
  }

  isRepliedToSelf(replyTo: any): boolean {
    if (!replyTo) return false;
    const senderId = replyTo.sender_id || replyTo.senderId || replyTo.userId || replyTo.sid || replyTo.meta?.sender_id;
    if (this.userId && senderId && String(senderId).trim() === String(this.userId).trim()) {
      return true;
    }

    const rawName = (replyTo.senderName || replyTo.name || '').trim();
    if (!rawName) return false;
    const lowerName = rawName.toLowerCase();

    if (lowerName === 'you') {
      const isSenderSelf = this.userId && this.message?.sender_id && String(this.userId).trim() === String(this.message.sender_id).trim();
      return isSenderSelf;
    }

    const selfNames: string[] = [
      this.userNameMapping?.[this.userId],
      this.chatMap?.[this.userId]?.displayName,
      this.chatMap?.[this.userId]?.userName,
      this.chatMap?.[this.userId]?.name,
      typeof localStorage !== 'undefined' ? localStorage.getItem('displayName') : null,
      typeof localStorage !== 'undefined' ? localStorage.getItem('username') : null,
      typeof localStorage !== 'undefined' ? localStorage.getItem('userName') : null,
      typeof localStorage !== 'undefined' ? localStorage.getItem('name') : null
    ].filter((n): n is string => !!n && typeof n === 'string').map(n => n.trim().toLowerCase());

    return selfNames.some(own => own === lowerName);
  }

  getReplySenderName(replyTo: any): string {
    if (!replyTo) return 'User';
    if (this.isRepliedToSelf(replyTo)) {
      return 'You';
    }
    if (String(replyTo.senderName).trim().toLowerCase() === 'you' && this.userId && this.message?.sender_id && String(this.userId).trim() !== String(this.message.sender_id).trim()) {
      return this.userNameMapping?.[this.message.sender_id] || this.message.senderName || 'User';
    }
    return replyTo.senderName || replyTo.name || 'User';
  }

  getReplyThumbnail(msg: any): string | null {
    return getReplyThumbnail(msg);
  }

  getReplyMediaType(msg: any): string {
    return getReplyMediaType(msg);
  }

  getReplyMessageText(msg: any): string {
    return getReplyMessageText(msg);
  }


  getReplyMediaTypeIcon(msg: any): string {
    return getReplyMediaTypeIcon(msg);
  }


  getReplyDuration(msg: any): string {
    if (!msg) return '';
    const duration = msg.meta?.duration || msg.duration;
    if (!duration) return '';
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  isMessageRedundantWithFile(message: any): boolean {
    return isMessageRedundantWithFile(message);
  }


  isGifMessage(message: any): boolean {
    return isGifMessage(message);
  }

  getGifUrl(message: any): string {
    if (!message) return '';
    const meta = getMessageMeta(message);
    const url = message.url || meta.gifUrl || meta.url || meta.fileUrl;
    return Array.isArray(url) ? url[0] : url || '';
  }

  handleGifError(event: any, message: any): void {
    if (message) {
      message._gifError = true;
    }
  }

  getFilesFromMessage(message: any): any[] {
    if (!message || this.isGifMessage(message)) return [];
    if (message._processedFiles) return message._processedFiles;

    let files: any[] = [];
    // Forwarded carousels keep every file in meta.files; history loaders rebuild `files` from one url.
    if (message.meta?.files?.length > (message.files?.length || 0)) {
      files = message.meta.files;
    } else if (message.files && message.files.length > 0) {
      files = message.files;
    } else if (message.meta?.files && message.meta.files.length > 0) {
      files = message.meta.files;
    } else {
      const url = message.url || message.meta?.url;
      if (Array.isArray(url) && url.length > 0) {
        const meta = getMessageMeta(message);
        files = url.map((u, i) => ({
          fileUrl: u,
          type: this.isImage(u) ? 'image/jpeg' : this.isVideo(u) ? 'video/mp4' : this.isAudio(u) ? 'audio/mpeg' : (this.isDocument(u) ? this.getFileType(u) : 'application/pdf'),
          name: (i === 0 ? meta.ogFileName : null) || this.getCleanFileName(u)
        }));
      } else {
        const singleUrl = message.url || message.meta?.fileUrl || message.meta?.url;
        if (singleUrl) {
          const meta = getMessageMeta(message);
          files = [{
            fileUrl: singleUrl,
            type: meta.fileType || this.getFileType(singleUrl),
            name: meta.ogFileName || this.getCleanFileName(singleUrl)
          }];
        }
      }
    }

    const processed = files.map((f, i) => {
      const url = f.fileUrl || f.url;
      let fileType = f.type || 'application/octet-stream';

      // Only derive type from URL if it's currently generic
      if (url && (fileType === 'application/octet-stream' || !fileType || fileType.startsWith('video/'))) {
        if (this.isDocument(url)) fileType = this.getFileType(url);
        else if (isVoiceMessage(url)) fileType = 'audio/mpeg';
        else if (this.isVideo(url)) fileType = 'video/mp4';
        else if (this.isImage(url)) fileType = 'image/jpeg';
        else if (this.isAudio(url)) fileType = 'audio/mpeg';
      }

      const meta = getMessageMeta(message);
      return {
        ...f,
        type: fileType,
        fileUrl: url,
        size: f.size || f.fileSize || (i === 0 ? (meta.size || meta.fileSize) : undefined),
        name: this.getDisplayFileName(f.ogFileName || (i === 0 ? meta.ogFileName : null) || f.name || url)
      };
    });

    message._processedFiles = processed;
    if (message.currentSlideIndex === undefined) message.currentSlideIndex = 0;
    return processed;
  }

  getDisplayFileName(fileNameOrUrl: any): string {
    if (!fileNameOrUrl) return 'File';
    if (typeof fileNameOrUrl === 'object') {
      fileNameOrUrl = fileNameOrUrl.ogFileName || fileNameOrUrl.name || fileNameOrUrl.fileUrl || fileNameOrUrl.url || '';
    }
    return stripTimestampPrefix(getCleanFileName(fileNameOrUrl));
  }

  getFileExtLabel(fileNameOrUrl: any): string {
    return getFileExtLabel(fileNameOrUrl);
  }

  getFileBadgeColor(fileNameOrUrl: any): string {
    return getFileBadgeColor(fileNameOrUrl);
  }

  isDocumentFile(file: any): boolean {
    if (!file) return false;
    const url = (file.fileUrl || file.url || '').toLowerCase();
    const name = (file.name || file.ogFileName || '').toLowerCase();
    const type = (file.type || '').toLowerCase();

    if (isVoiceMessage(url) || isVoiceMessage(name)) return false;
    if (type.startsWith('image/') || this.isImage(url) || this.isImage(name)) return false;
    if (DOCUMENT_EXTENSIONS.some(ext => (name || url).endsWith(ext.toLowerCase()))) return true;
    if (type.startsWith('video/') || this.isVideo(url)) return false;
    if (type.startsWith('audio/') || this.isAudio(url, name)) return false;
    return this.isDocument(url) || this.isDocument(name);
  }

  hasOnlyDocuments(message: any): boolean {
    const files = this.getFilesFromMessage(message);
    if (!files || files.length === 0) return false;
    return files.every(f => this.isDocumentFile(f));
  }

  downloadFile(file: any, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const url = file?.fileUrl || file?.url;
    if (!url) return;
    const filename = this.getDisplayFileName(file.name || file.ogFileName || url);
    // Fetch + blob save keeps the download inside the installed PWA instead of opening the browser.
    saveFileInApp(url, filename);
  }

  /** Opens a shared document in the in-app preview; the row's download button still downloads directly. */
  previewDocument(file: any, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const url = file?.fileUrl || file?.url;
    if (!url) return;
    openDocumentPreview(this.dialog, {
      fileUrl: url,
      name: this.getDisplayFileName(file.name || file.ogFileName || url),
      sizeLabel: this.formatSize(file.size),
    });
  }

  downloadAudioFile(file?: any, event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const defaultName = this.isAudioVoiceMessage(file) ? 'voice_message.ogg' : 'audio.mp3';
    const target = file || (this.getFilesFromMessage(this.message) && this.getFilesFromMessage(this.message)[0]) || {
      fileUrl: this.getAudioFileUrl(file),
      name: this.message?.meta?.ogFileName || this.message?.meta?.fileName || this.message?.ogFileName || defaultName
    };
    this.downloadFile(target, event);
  }

  getCleanFileName(url: string): string {
    return getCleanFileName(url);
  }


  getAggregatedReactions(reactions: any[]): any[] {
    return getAggregatedReactions(reactions);
  }


  getDisplayedReactions(reactions: any[]): any[] {
    return this.getAggregatedReactions(reactions).slice(0, 3);
  }

  getRemainingReactionCount(reactions: any[]): number {
    const aggregated = this.getAggregatedReactions(reactions);
    if (aggregated.length <= 3) return 0;
    return aggregated.slice(3).reduce((sum, item) => sum + item.count, 0);
  }

  hasUserReacted(reactions: any[], emoji: string): boolean {
    if (!reactions || reactions.length === 0) return false;
    return reactions.some((r: any) => {
      let rEmoji = r;
      if (typeof r === 'object' && r !== null) {
        rEmoji = r.emoji || r.emote || '';
      }
      const userId = r.user_id || r.userId || r.sender_id;
      return rEmoji === emoji && userId === this.userId;
    });
  }

  onReactionChipClick(emoji: string, event: MouseEvent) {
    event.stopPropagation();
    this.reaction.emit({ emoji, messageId: this.message._id || this.message.id });
  }

  handleProfileImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/user-icon.png';
  }

  toggleMessageExpand(messageId: string) {
    if (!messageId) return;
    if (this.expandedMessages.has(messageId)) {
      this.expandedMessages.delete(messageId);
    } else {
      this.expandedMessages.add(messageId);
    }
  }

  isMessageExpanded(messageId: string): boolean {
    if (!messageId) return false;
    return this.expandedMessages.has(messageId);
  }

  getDisplayMessage(text: string, messageId: string): any {
    if (!text) return '';

    const isExpanded = this.isMessageExpanded(messageId);
    const meta = getMessageMeta(this.message);
    const mentions = meta?.mentions || [];
    const mentionsKey = mentions.map((m: any) => `${m?.name || ''}:${m?.position || ''}`).join('|');
    const cacheKey = `${messageId || ''}__${isExpanded ? 1 : 0}__${mentionsKey}__${text}`;

    if (this.displayMessageCache.has(cacheKey)) {
      return this.displayMessageCache.get(cacheKey);
    }

    let displayText = text;
    if (this.isMessageLong(text) && !isExpanded) {
      displayText = this.truncateSafely(text, 300);
    }

    // 1. Escape original text first for safety
    displayText = this.escapeHtml(displayText);

    // 2. Identify URLs and replace with placeholders to avoid conflicts with mentions
    const links: { placeholder: string; html: string }[] = [];
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    displayText = displayText.replace(urlRegex, (url) => {
      // Clean trailing punctuation
      let cleanUrl = url;
      let trailing = '';
      const punctuation = [',', '.', '!', '?', ')', ']', '}'];
      while (cleanUrl.length > 0 && punctuation.includes(cleanUrl[cleanUrl.length - 1])) {
        if (cleanUrl[cleanUrl.length - 1] === ')' && cleanUrl.includes('(')) break;
        if (cleanUrl[cleanUrl.length - 1] === ']' && cleanUrl.includes('[')) break;
        if (cleanUrl[cleanUrl.length - 1] === '}' && cleanUrl.includes('{')) break;
        trailing = cleanUrl[cleanUrl.length - 1] + trailing;
        cleanUrl = cleanUrl.substring(0, cleanUrl.length - 1);
      }

      const placeholder = `\uE000LINK_${links.length}\uE001`;
      const html = `<a href="${cleanUrl}" target="_blank" class="link-url">${cleanUrl}</a>`;
      links.push({ placeholder, html });
      return placeholder + trailing;
    });

    // 2.5. Identify email addresses and replace with placeholders so they are never treated as mentions
    const emails: { placeholder: string; text: string }[] = [];
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

    displayText = displayText.replace(emailRegex, (email) => {
      const placeholder = `\uE000EMAIL_${emails.length}\uE001`;
      emails.push({ placeholder, text: email });
      return placeholder;
    });

    // 3. Handle Mentions
    if (mentions.length > 0) {
      const names = mentions.map((m: any) => m.name).filter(Boolean);
      if (/(^|[^\w.+-])@all\b/i.test(displayText) && !names.some((n: string) => n.toLowerCase() === 'all')) {
        names.push('all');
      }
      const mentionNames = Array.from(new Set(names))
        .sort((a: any, b: any) => (b as string).length - (a as string).length);

      mentionNames.forEach(name => {
        if (!name) return;
        const isAll = (name as string).toLowerCase() === 'all';
        const mentionObj = mentions.find((m: any) => (m.name || '').toLowerCase() === (name as string).toLowerCase());
        const targetUserId = mentionObj?.userId || (isAll ? 'all' : '');
        const escapedName = this.escapeHtml(name as string);
        const regexName = (name as string).replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        const mentionRegex = new RegExp(`(^|[^\\w.+-])@${regexName}\\b`, 'gi');
        if (isAll) {
          displayText = displayText.replace(mentionRegex, `$1<span class="mention-highlight mention-all-highlight" data-mention-type="all">@${escapedName}</span>`);
        } else {
          displayText = displayText.replace(mentionRegex, `$1<span class="mention-highlight" data-mention-type="user" data-user-id="${targetUserId}" data-mention-name="${escapedName}">@${escapedName}</span>`);
        }
      });
    } else {
      const allRegex = /(^|[^\w.+-])@all\b/gi;
      displayText = displayText.replace(allRegex, '$1<span class="mention-highlight mention-all-highlight" data-mention-type="all">@all</span>');

      const genericMentionRegex = /(^|[^\w.+-])@([a-zA-Z0-9_]+(?:\.[a-zA-Z0-9_]+)*)\b/g;
      displayText = displayText.replace(genericMentionRegex, (match, prefix, username) => {
        if (!username) return match;
        const lower = username.toLowerCase();
        if (lower === 'all') {
          return match;
        }
        // Do not treat domain names / email fragments as user mentions
        if (/\.(com|org|net|edu|gov|io|co|in|ai|app|dev|xyz|me|info|biz|uk|us|ca|au|de|fr|tech|site|online)$/i.test(lower)) {
          return match;
        }
        if (/\.[a-z]{2,6}$/i.test(lower)) {
          return match;
        }
        return `${prefix}<span class="mention-highlight" data-mention-type="user" data-mention-name="${this.escapeHtml(username)}">@${this.escapeHtml(username)}</span>`;
      });
    }

    // 3.5. Parse Telegram-style Markdown & formatting
    // Shield code blocks and inline code from formatting and spoiler regexes
    const codeBlocks: string[] = [];
    displayText = displayText.replace(/```(?:[a-zA-Z0-9_-]+)?\n?([\s\S]*?)```/g, (m, code) => {
      const idx = codeBlocks.length;
      codeBlocks.push(`<pre class="tg-code-block"><code>${code}</code></pre>`);
      return `\uE002CODE_BLOCK_${idx}\uE003`;
    });

    const inlineCodes: string[] = [];
    displayText = displayText.replace(/`([^`\n]+)`/g, (m, code) => {
      const idx = inlineCodes.length;
      inlineCodes.push(`<code class="tg-inline-code">${code}</code>`);
      return `\uE004INLINE_CODE_${idx}\uE005`;
    });

    // Spoilers / Hidden text: ||([\s\S]+?)|| or [spoiler]([\s\S]+?)[/spoiler]
    let spoilerIndex = 0;
    const renderSpoiler = (content: string) => {
      const spoilerKey = `${messageId || ''}_${spoilerIndex++}`;
      const isRevealed = this.revealedSpoilers.has(spoilerKey);
      return `<span class="tg-spoiler${isRevealed ? ' revealed' : ''}" data-spoiler-key="${spoilerKey}" title="${isRevealed ? 'Click to hide' : 'Click to reveal'}">${content}</span>`;
    };
    displayText = displayText.replace(/\|\|([\s\S]+?)\|\|/g, (m, content) => renderSpoiler(content));
    displayText = displayText.replace(/\[spoiler\]([\s\S]+?)\[\/spoiler\]/gi, (m, content) => renderSpoiler(content));

    // Bold: **text** or *text*
    displayText = displayText.replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>');
    displayText = displayText.replace(/(^|[^\w*])\*([^*\n\s][^*\n]*[^*\n\s]|\S)\*(?=[^\w*]|$)/g, '$1<b>$2</b>');

    // Italic: __text__ or _text_
    displayText = displayText.replace(/__([^_\n]+)__/g, '<i>$1</i>');
    displayText = displayText.replace(/(^|[^\w_])_([^_\n\s][^_\n]*[^_\n\s]|\S)_(?=[^\w_]|$)/g, '$1<i>$2</i>');

    // Strikethrough: ~text~
    displayText = displayText.replace(/(^|[^\w~])~([^~\n\s][^~\n]*[^~\n\s]|\S)~(?=[^\w~]|$)/g, '$1<s>$2</s>');

    // Blockquote: lines starting with &gt;
    displayText = displayText.replace(/(?:^|\n)&gt;\s?(.*)/g, '<blockquote class="tg-quote">$1</blockquote>');

    // Restore shielded inline code and code blocks
    inlineCodes.forEach((codeHtml, idx) => {
      displayText = displayText.split(`\uE004INLINE_CODE_${idx}\uE005`).join(codeHtml);
    });
    codeBlocks.forEach((codeHtml, idx) => {
      displayText = displayText.split(`\uE002CODE_BLOCK_${idx}\uE003`).join(codeHtml);
    });

    // 4. Restore email addresses and URLs from placeholders
    emails.forEach(email => {
      displayText = displayText.split(email.placeholder).join(email.text);
    });

    links.forEach(link => {
      displayText = displayText.split(link.placeholder).join(link.html);
    });

    const safeHtml = this.sanitizer.bypassSecurityTrustHtml(displayText);
    this.displayMessageCache.set(cacheKey, safeHtml);
    return safeHtml;
  }

  escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  isMessageLong(text: string): boolean {
    return text && text.length > 300;
  }

  truncateSafely(str: string, maxLen: number): string {
    if (!str || str.length <= maxLen) return str;
    let cut = str.substring(0, maxLen);

    // If cut inside ||...|| spoiler, prevent leaking the hidden spoiler content
    const pipePairs = (cut.match(/\|\|/g) || []).length;
    if (pipePairs % 2 !== 0) {
      const rest = str.substring(maxLen);
      const closeIdx = rest.indexOf('||');
      if (closeIdx !== -1 && closeIdx < 80) {
        cut += rest.substring(0, closeIdx + 2);
      } else {
        const lastOpen = cut.lastIndexOf('||');
        cut = lastOpen > 0 ? cut.substring(0, lastOpen).trim() : cut + '||';
      }
    }

    // If cut inside [spoiler]...[/spoiler]
    const openSpoilers = (cut.match(/\[spoiler\]/gi) || []).length;
    const closeSpoilers = (cut.match(/\[\/spoiler\]/gi) || []).length;
    if (openSpoilers > closeSpoilers) {
      const lastOpen = cut.toLowerCase().lastIndexOf('[spoiler]');
      cut = lastOpen > 0 ? cut.substring(0, lastOpen).trim() : cut + '[/spoiler]';
    }

    return cut.trim() + '...';
  }

  onImageClick(file: any, index: number = 0) {
    const files = this.getFilesFromMessage(this.message)
      .map(f => ({
        fileUrl: f.fileUrl || f.url,
        type: f.type,
        name: f.fileName || f.name || this.message?.message || 'file'
      }))
      .filter(f => !!f.fileUrl);

    this.dialog.open(ImageViewerDialogComponent, {
      data: { files, startIndex: index },
      panelClass: "image-viewer-dialog-panel",
      backdropClass: "image-viewer-dialog-backdrop",
      height: "100vh",
      width: "100vw",
      maxHeight: "100vh",
      maxWidth: "100vw",
      autoFocus: false,
    });
  }

  onDownload(file: any, event?: MouseEvent) {
    this.downloadFile(file, event);
  }

  formatSize(bytes: any): string {
    if (!bytes && bytes !== 0) return '';
    const num = typeof bytes === 'string' ? parseFloat(bytes) : bytes;
    if (isNaN(num) || num < 0) return '';
    if (num === 0) return '0B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(num) / Math.log(k));
    const val = (num / Math.pow(k, i)).toFixed(1);
    return `${val}${sizes[i]}`;
  }

  prevSlide(message: any) {
    const files = this.getFilesFromMessage(message);
    const current = message.currentSlideIndex || 0;
    message.currentSlideIndex = current > 0 ? current - 1 : files.length - 1;
  }

  nextSlide(message: any) {
    const files = this.getFilesFromMessage(message);
    const current = message.currentSlideIndex || 0;
    message.currentSlideIndex = current < files.length - 1 ? current + 1 : 0;
  }

  goToSlide(message: any, index: number) {
    message.currentSlideIndex = index;
  }

  openUrl(url: string) {
    window.open(url, '_blank');
  }

  hasLinkMeta(message: any): boolean {
    const meta = message?.meta;
    if (!meta) return false;

    const title = (meta.title || '').trim();
    const description = (meta.description || '').trim();
    const image = (meta.image || '').trim();
    const icon = (meta.icon || '').trim();

    const isImageValid = !!(image && !meta.imageBroken);
    const isIconValid = !!(icon && !meta.iconBroken);

    const genericTitles = [
      'just a moment...',
      'waiting...',
      'checking your browser...',
      'access denied',
      '403 forbidden',
      '404 not found',
      'error',
      'loading...'
    ];

    const lowerTitle = title.toLowerCase();
    const isGeneric = genericTitles.some(t => lowerTitle.includes(t));

    // Final check: a generic title + no description + no big image = NO PREVIEW.
    // We ignore the small icon because it's usually just a fallback that's not worth a box on its own.
    const shouldShow = !(isGeneric && !description && !isImageValid) && !!(title || description || isImageValid || isIconValid);

    return shouldShow;
  }

  isIconMode(message: any): boolean {
    const meta = message?.meta;
    if (!meta) return false;

    const image = (meta.image || '').trim();
    const icon = (meta.icon || '').trim();

    const isImageValid = !!(image && !meta.imageBroken);
    const isIconValid = !!(icon && !meta.iconBroken);

    if (!isIconValid) return false;

    // If no big image, it's definitely icon mode
    if (!isImageValid) return true;

    // If image and icon are the same, it's almost certainly a favicon, so use icon mode
    if (image === icon) return true;

    // If the image URL itself suggests it's just a favicon
    const lowerImage = image.toLowerCase();
    if (lowerImage.includes('favicon') || lowerImage.endsWith('.ico')) {
      return true;
    }

    return false;
  }

  onPreviewImageError(message: any) {
    if (message.meta) {
      message.meta.imageBroken = true;
      this.cdRef.detectChanges();
    }
  }

  onPreviewIconError(message: any) {
    if (message.meta) {
      message.meta.iconBroken = true;
      this.cdRef.detectChanges();
    }
  }

  isMessageStarred(): boolean {
    if (this.message?.isStarred || this.message?.meta?.isStarred || !!(this.message?.starredMessageId || this.message?.meta?.starredMessageId)) {
      return true;
    }

    if ((this.message?.starredMessages || []).some((s: any) => String(s.userId) === String(this.userId))) {
      return true;
    }

    const messageId = String(this.message?._id || this.message?.id || this.message?.messageId || '');
    const carouselIdsSet = new Set<string>();
    if (messageId && messageId !== 'undefined' && messageId !== 'null') {
      carouselIdsSet.add(messageId);
    }

    if (this.message?._allIds) {
      this.message._allIds.forEach((id: string) => {
        if (id && id !== 'undefined' && id !== 'null') carouselIdsSet.add(String(id));
      });
    }
    if (this.message?.files) {
      this.message.files.forEach((f: any) => {
        const id = String(f._id || f.id || '');
        if (id && id !== 'undefined' && id !== 'null') carouselIdsSet.add(id);
      });
    }

    const carouselIds = Array.from(carouselIdsSet);
    if (carouselIds.length === 0 || !this.starredMessages || this.starredMessages.length === 0) return false;

    // Check if any record in starredMessages refers to ANY of our valid IDs
    return this.starredMessages.some(m => {
      const smMessageId = String(m.messageId || '');
      const smId = String(m._id || m.id || '');
      const nestedId = String(m.message?._id || m.message?.id || m.message?.messageId || '');
      const metaId = String(m.message?.meta?.id || '');

      return (smMessageId && smMessageId !== 'undefined' && smMessageId !== 'null' && carouselIds.includes(smMessageId)) ||
        (smId && smId !== 'undefined' && smId !== 'null' && carouselIds.includes(smId)) ||
        (nestedId && nestedId !== 'undefined' && nestedId !== 'null' && carouselIds.includes(nestedId)) ||
        (metaId && metaId !== 'undefined' && metaId !== 'null' && carouselIds.includes(metaId));
    });
  }


  // ==================== SWIPE TO REPLY LOGIC ====================
  swipeTranslateX: number = 0;
  isSwiping: boolean = false;
  isSwipeThresholdReached: boolean = false;
  isAnimatingBack: boolean = false;

  private swipeStartX: number = 0;
  private swipeStartY: number = 0;
  private swipeStartTime: number = 0;
  private isScrollDirectionLocked: boolean = false;
  private isHorizontalSwipeLocked: boolean = false;
  private justSwiped: boolean = false;
  private readonly SWIPE_THRESHOLD: number = 55;
  private readonly SWIPE_MAX: number = 85;

  private mouseMoveListener?: (e: MouseEvent) => void;
  private mouseUpListener?: (e: MouseEvent) => void;

  ngOnDestroy(): void {
    this.cleanupMouseListeners();
  }

  onTouchStart(e: TouchEvent): void {
    if (this.isSystemMessage(this.message) || this.message?.isUploading) return;
    if (e.touches.length !== 1) return;

    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, audio, video, .menu-trigger, .tg-spoiler, .reaction-chip, .reaction-picker, .reactions-row, .mention-chip, .user-mention, .tg-doc-download-btn, .carousel-control')) {
      return;
    }

    const touch = e.touches[0];
    this.swipeStartX = touch.clientX;
    this.swipeStartY = touch.clientY;
    this.swipeStartTime = Date.now();
    this.isScrollDirectionLocked = false;
    this.isHorizontalSwipeLocked = false;
    this.isSwiping = false;
    this.isSwipeThresholdReached = false;
    this.swipeTranslateX = 0;
  }

  onTouchMove(e: TouchEvent): void {
    if (this.isScrollDirectionLocked || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - this.swipeStartX;
    const deltaY = touch.clientY - this.swipeStartY;

    if (!this.isHorizontalSwipeLocked) {
      if (Math.abs(deltaY) > 7 && Math.abs(deltaY) >= Math.abs(deltaX)) {
        this.isScrollDirectionLocked = true;
        return;
      }
      if (deltaX > 7 && deltaX > Math.abs(deltaY)) {
        this.isHorizontalSwipeLocked = true;
        this.isSwiping = true;
      } else if (deltaX < -7) {
        this.isScrollDirectionLocked = true;
        return;
      } else {
        return;
      }
    }

    if (this.isHorizontalSwipeLocked && deltaX > 0) {
      if (e.cancelable) {
        e.preventDefault();
      }

      let currentX = 0;
      if (deltaX <= this.SWIPE_THRESHOLD) {
        currentX = deltaX * 0.85;
      } else {
        currentX = (this.SWIPE_THRESHOLD * 0.85) + ((deltaX - this.SWIPE_THRESHOLD) * 0.35);
      }
      this.swipeTranslateX = Math.min(currentX, this.SWIPE_MAX);

      const crossed = this.swipeTranslateX >= (this.SWIPE_THRESHOLD * 0.8);
      if (crossed && !this.isSwipeThresholdReached) {
        this.isSwipeThresholdReached = true;
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          try { navigator.vibrate(15); } catch (_) { }
        }
      } else if (!crossed && this.isSwipeThresholdReached) {
        this.isSwipeThresholdReached = false;
      }
      this.cdRef.markForCheck();
    }
  }

  onTouchEnd(e: TouchEvent): void {
    if (!this.isHorizontalSwipeLocked && !this.isSwiping) {
      this.resetSwipeState();
      return;
    }

    const duration = Date.now() - this.swipeStartTime;
    const isFlick = duration < 250 && this.swipeTranslateX > 30;
    const shouldReply = this.isSwipeThresholdReached || isFlick;

    if (shouldReply) {
      this.triggerSwipeReply();
    }

    this.resetSwipeWithAnimation();
  }

  onTouchCancel(): void {
    this.resetSwipeWithAnimation();
  }

  onMouseDown(e: MouseEvent): void {
    if (this.isSystemMessage(this.message) || this.message?.isUploading) return;
    if (e.button !== 0) return;

    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, audio, video, .menu-trigger, .tg-spoiler, .reaction-chip, .reaction-picker, .reactions-row, .mention-chip, .user-mention, .tg-doc-download-btn, .carousel-control, .text-muted')) {
      return;
    }

    this.swipeStartX = e.clientX;
    this.swipeStartY = e.clientY;
    this.swipeStartTime = Date.now();
    this.isScrollDirectionLocked = false;
    this.isHorizontalSwipeLocked = false;
    this.isSwiping = false;
    this.isSwipeThresholdReached = false;
    this.swipeTranslateX = 0;

    this.mouseMoveListener = (me: MouseEvent) => this.onMouseMove(me);
    this.mouseUpListener = (me: MouseEvent) => this.onMouseUp(me);
    window.addEventListener('mousemove', this.mouseMoveListener);
    window.addEventListener('mouseup', this.mouseUpListener);
  }

  private onMouseMove(e: MouseEvent): void {
    if (this.isScrollDirectionLocked) return;

    const deltaX = e.clientX - this.swipeStartX;
    const deltaY = e.clientY - this.swipeStartY;

    if (!this.isHorizontalSwipeLocked) {
      if (Math.abs(deltaY) > 8 && Math.abs(deltaY) >= Math.abs(deltaX)) {
        this.isScrollDirectionLocked = true;
        this.cleanupMouseListeners();
        return;
      }
      if (deltaX > 10 && deltaX > Math.abs(deltaY) * 1.2) {
        this.isHorizontalSwipeLocked = true;
        this.isSwiping = true;
        try { window.getSelection()?.removeAllRanges(); } catch (_) { }
      } else if (deltaX < -10) {
        this.isScrollDirectionLocked = true;
        this.cleanupMouseListeners();
        return;
      } else {
        return;
      }
    }

    if (this.isHorizontalSwipeLocked && deltaX > 0) {
      let currentX = 0;
      if (deltaX <= this.SWIPE_THRESHOLD) {
        currentX = deltaX * 0.85;
      } else {
        currentX = (this.SWIPE_THRESHOLD * 0.85) + ((deltaX - this.SWIPE_THRESHOLD) * 0.35);
      }
      this.swipeTranslateX = Math.min(currentX, this.SWIPE_MAX);

      const crossed = this.swipeTranslateX >= (this.SWIPE_THRESHOLD * 0.8);
      if (crossed && !this.isSwipeThresholdReached) {
        this.isSwipeThresholdReached = true;
      } else if (!crossed && this.isSwipeThresholdReached) {
        this.isSwipeThresholdReached = false;
      }
      this.cdRef.markForCheck();
    }
  }

  private onMouseUp(e: MouseEvent): void {
    this.cleanupMouseListeners();
    if (!this.isHorizontalSwipeLocked && !this.isSwiping) {
      this.resetSwipeState();
      return;
    }

    const duration = Date.now() - this.swipeStartTime;
    const isFlick = duration < 250 && this.swipeTranslateX > 30;
    const shouldReply = this.isSwipeThresholdReached || isFlick;

    if (shouldReply) {
      this.triggerSwipeReply();
    }

    this.resetSwipeWithAnimation();
  }

  private cleanupMouseListeners(): void {
    if (this.mouseMoveListener) {
      window.removeEventListener('mousemove', this.mouseMoveListener);
      this.mouseMoveListener = undefined;
    }
    if (this.mouseUpListener) {
      window.removeEventListener('mouseup', this.mouseUpListener);
      this.mouseUpListener = undefined;
    }
  }

  private triggerSwipeReply(): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate([15, 30, 20]); } catch (_) { }
    }
    this.reply.emit(this.message);
  }

  private resetSwipeWithAnimation(): void {
    if (this.isSwiping) {
      this.justSwiped = true;
      setTimeout(() => { this.justSwiped = false; }, 140);
    }
    this.isAnimatingBack = true;
    this.swipeTranslateX = 0;
    this.isSwipeThresholdReached = false;
    this.cdRef.markForCheck();

    setTimeout(() => {
      this.isAnimatingBack = false;
      this.resetSwipeState();
      this.cdRef.markForCheck();
    }, 280);
  }

  private resetSwipeState(): void {
    this.isSwiping = false;
    this.isHorizontalSwipeLocked = false;
    this.isScrollDirectionLocked = false;
    this.swipeTranslateX = 0;
    this.isSwipeThresholdReached = false;
  }

  getSwipeTransform(): string {
    if (this.swipeTranslateX > 0) {
      return `translateX(${this.swipeTranslateX}px)`;
    }
    return '';
  }

  getSwipeTransition(): string {
    if (this.isAnimatingBack) {
      return 'transform 0.28s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
    }
    return this.isSwiping ? 'none' : '';
  }

  getSwipeIndicatorOpacity(): number {
    if (this.swipeTranslateX <= 4) return 0;
    return Math.min(this.swipeTranslateX / 35, 1);
  }

  getSwipeIndicatorTransform(): string {
    const scale = this.isSwipeThresholdReached
      ? 1.15
      : Math.min(0.4 + (this.swipeTranslateX / 55) * 0.6, 1.0);
    const rotate = Math.max(-20 + (this.swipeTranslateX / 55) * 20, 0);
    return `translateY(-50%) scale(${scale}) rotate(${rotate}deg)`;
  }

  onContentBoxClick(e: MouseEvent): void {
    if (this.justSwiped) {
      e.stopPropagation();
      e.preventDefault();
      this.justSwiped = false;
    }
  }

}
